import React, { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { prefixClass } from 'lib/utils';
import { IconTaskNew } from 'components/Icons';
import { AskRequest, AskRequestCreateType, AskRequestStatus } from '@hone-automation/common';
import { useAuthContext } from 'context/useAuthContext';
import { useQueueContext } from 'presentation/components/Queue/QueueContext';
import { useCreateQueueRequestMutation } from 'hooks/queue';
import { useEvent } from 'hooks/useEvent';
import KSInputField from 'presentation/components/KSInputField';
import KSSelector from 'presentation/components/KSSelector';
import KSCheckBox from 'presentation/components/KSCheckBox';
import CMComposer from 'presentation/components/Queue/CMComposer/CMComposer';
import { CMComposerContexts } from 'presentation/components/Queue/CMComposer/types';
import { MobileSlideUpPanel } from '../MobileSlideUpPanel/MobileSlideUpPanel';
import './MobileCreateRequestPanel.scss';

interface MobileCreateRequestPanelProps {
  isOpen: boolean;
  onClose: () => void;
  locationName?: string;
}

const assignOptions = [
  { label: 'Customer', value: 'Open' },
  { label: 'Accountant', value: 'In Progress' },
];

export const MobileCreateRequestPanel: React.FC<MobileCreateRequestPanelProps> = ({
  isOpen,
  onClose,
  locationName,
}) => {
  const prefix = prefixClass('mobile-create-request-panel');
  const { locationId } = useParams();
  const { isBookkeeperAdmin } = useAuthContext();
  const { dispatch } = useQueueContext();
  const { dispatch: emitEvent } = useEvent('onQueueRequestChangeStatus');

  const [identifier, setIdentifier] = useState('');
  const [forValue, setForValue] = useState<AskRequestStatus>(isBookkeeperAdmin ? 'Open' : 'In Progress');
  const [isInternal, setIsInternal] = useState(false);

  const handleReset = useCallback(() => {
    setIdentifier('');
    setForValue(isBookkeeperAdmin ? 'Open' : 'In Progress');
    setIsInternal(false);
  }, [isBookkeeperAdmin]);

  const { mutate: createRequest, isPending } = useCreateQueueRequestMutation(
    locationId || '',
    (newRequest: AskRequest) => {
      dispatch({ type: 'ADD_NEW_REQUEST', payload: { request: newRequest, status: newRequest.status } });
      emitEvent({ field: newRequest.status, duration: 5000 });
      handleReset();
      onClose();
    }
  );

  const handleClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleSubmit = useCallback(
    (formData: FormData, createType: AskRequestCreateType) => {
      formData.append('identifier', identifier);
      formData.append('createType', createType);
      formData.append('type', 'Customer Request');
      createRequest(formData as any);
    },
    [identifier, createRequest]
  );

  return (
    <MobileSlideUpPanel
      isOpen={isOpen}
      onClose={handleClose}
      title="New Queue Request"
      titleIcon={<IconTaskNew />}
      fullSizeOnOpen
      resizable={false}
    >
      <div className={prefix()}>
        <div className={prefix('__meta')}>
          {locationName && <span className={prefix('__location')}>{locationName}</span>}
        </div>

        <div className={prefix('__form')}>
          <div className={prefix('__row')}>
            <label className={prefix('__label')}>Title</label>
            <KSInputField
              value={identifier}
              placeholder="New request title"
              onChange={e => setIdentifier(e.target.value)}
            />
          </div>

          {isBookkeeperAdmin && (
            <>
              <div className={prefix('__row')}>
                <label className={prefix('__label')}>Assign to</label>
                <KSSelector
                  options={assignOptions}
                  value={forValue}
                  onChange={option => {
                    setForValue(option as AskRequestStatus);
                    if (option === 'Open') setIsInternal(false);
                  }}
                />
              </div>

              {forValue !== 'Open' && (
                <div className={prefix('__frame')}>
                  <KSCheckBox
                    rounded
                    label="Internal (Visible to KS Only)"
                    checked={isInternal}
                    onChange={checked => setIsInternal(checked)}
                    colorLabel="gray"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className={prefix('__composer')}>
          <CMComposer
            handleOnCallback={handleSubmit}
            hideReopenLabel
            messageLoading={isPending}
            sendType={forValue === 'Open' ? 'For Customer' : 'For Accountant'}
            context={CMComposerContexts.createRequest}
            isCreateMode
            isInternal={isInternal}
          />
        </div>
      </div>
    </MobileSlideUpPanel>
  );
};

MobileCreateRequestPanel.displayName = 'MobileCreateRequestPanel';

export default MobileCreateRequestPanel;
